/* ==========================================================================
   Valioza — theme.js
   Progressive enhancement only: every feature here degrades to plain HTML.
   ========================================================================== */
(function () {
  'use strict';

  var on = function (el, evt, fn) {
    if (el) el.addEventListener(evt, fn);
  };

  // init() also runs on shopify:section:load, so element-level listeners are
  // bound exactly once per element.
  var claim = function (el, key) {
    if (!el || el.dataset[key] === '1') return false;
    el.dataset[key] = '1';
    return true;
  };

  /* ------------------------------------------------------------------
     Mobile navigation drawer
     ------------------------------------------------------------------ */
  function initMobileNav() {
    var drawer = document.querySelector('[data-mobile-nav]');
    var openBtn = document.querySelector('[data-mobile-nav-open]');
    if (!drawer || !openBtn) return;
    if (!claim(openBtn, 'vzNav')) return;

    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      var focusable = drawer.querySelector('a, button');
      if (focusable) focusable.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (lastFocused) lastFocused.focus();
    }

    on(openBtn, 'click', open);

    Array.prototype.forEach.call(
      drawer.querySelectorAll('[data-mobile-nav-close]'),
      function (el) {
        on(el, 'click', close);
      }
    );

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ------------------------------------------------------------------
     Hero scroll cue — scrolls to whatever comes next
     ------------------------------------------------------------------ */
  function initScrollCue() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-scroll-next]'),
      function (btn) {
        if (!claim(btn, 'vzScroll')) return;
        on(btn, 'click', function () {
          // The section may be wrapped by Shopify, so walk up to the
          // outermost element that still has a next sibling.
          var node = btn.closest('section') || btn.parentElement;
          while (node && !node.nextElementSibling && node.parentElement) {
            node = node.parentElement;
          }

          var next = node && node.nextElementSibling;
          if (!next) {
            window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' });
            return;
          }

          var header = document.querySelector('[data-header]');
          var offset = header ? header.offsetHeight : 0;
          var top = next.getBoundingClientRect().top + window.pageYOffset - offset;

          window.scrollTo({ top: top, behavior: 'smooth' });
        });
      }
    );
  }

  /* ------------------------------------------------------------------
     Variant select — swap price without a page reload
     ------------------------------------------------------------------ */
  function initVariantSelects() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-variant-select]'),
      function (select) {
        if (!claim(select, 'vzVariant')) return;
        on(select, 'change', function () {
          var option = select.options[select.selectedIndex];
          if (!option) return;

          var scope = select.closest('.pdp__details') || document;
          var priceEl = scope.querySelector('[data-product-price]');
          var compareEl = scope.querySelector('[data-product-compare-price]');

          if (priceEl && option.dataset.price) {
            priceEl.textContent = option.dataset.price;
          }
          if (compareEl && option.dataset.comparePrice) {
            compareEl.textContent = option.dataset.comparePrice;
          }

          // Stock state differs per variant, so the buy button, the ship-date
          // note and the order-line property all follow the selection.
          var isPreorder = option.dataset.preorder === 'true';
          var isAvailable = option.dataset.available !== 'false';

          var button = scope.querySelector('[data-buy-button]');
          if (button) {
            button.disabled = !isAvailable;
            button.classList.toggle('btn--preorder', isPreorder);
            if (!isAvailable) {
              button.textContent = 'Sold out';
            } else {
              button.textContent = isPreorder
                ? button.dataset.labelPreorder
                : button.dataset.labelDefault;
            }
          }

          var note = scope.querySelector('[data-preorder-note]');
          if (note) note.hidden = !isPreorder;

          // A disabled input is not submitted, so the property is only
          // attached to genuine pre-order line items.
          var prop = scope.querySelector('[data-preorder-property]');
          if (prop) prop.disabled = !isPreorder;

          // Keep the URL shareable.
          if (window.history && window.history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', option.value);
            window.history.replaceState({}, '', url.toString());
          }
        });
      }
    );
  }

  /* ------------------------------------------------------------------
     AJAX add to cart (product page + card quick-add)
     ------------------------------------------------------------------ */
  function updateCartCount(count) {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-cart-count]'),
      function (el) {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      }
    );
  }

  function initAddToCart() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-type="add-to-cart-form"]'),
      function (form) {
        if (!claim(form, 'vzAtc')) return;
        on(form, 'submit', function (e) {
          e.preventDefault();

          var button = form.querySelector('[type="submit"]');
          if (!button || button.disabled) return;

          var label = button.textContent;
          button.disabled = true;
          button.textContent = 'Adding…';

          fetch(window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root + 'cart/add.js'
            : '/cart/add.js', {
            method: 'POST',
            body: new FormData(form),
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
            .then(function (res) {
              if (!res.ok) throw new Error('Add to cart failed');
              return fetch('/cart.js', {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
              });
            })
            .then(function (res) {
              return res.json();
            })
            .then(function (cart) {
              updateCartCount(cart.item_count);
              button.textContent = 'Added';
            })
            .catch(function (err) {
              console.error(err);
              button.textContent = 'Try again';
            })
            .finally(function () {
              window.setTimeout(function () {
                button.disabled = false;
                button.textContent = label;
              }, 1600);
            });
        });
      }
    );
  }

  /* ------------------------------------------------------------------
     Collection sort — navigate, preserving every other query param
     ------------------------------------------------------------------ */
  function initSort() {
    var select = document.querySelector('[data-sort-select]');
    if (!select || !claim(select, 'vzSort')) return;

    on(select, 'change', function () {
      if (!select.value) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ------------------------------------------------------------------
     Collection filter panel toggle
     ------------------------------------------------------------------ */
  function initFilterPanel() {
    var toggle = document.querySelector('[data-filter-toggle]');
    if (!toggle || !claim(toggle, 'vzFilter')) return;

    // Controls the storefront filter panel when one exists, otherwise the
    // tag pill row.
    var target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;

    on(toggle, 'click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      target.hidden = isOpen;
    });
  }

  /* ------------------------------------------------------------------
     Marquee — drop the duplicated track if it cannot overflow,
     otherwise the loop stutters on wide screens with few cards.
     ------------------------------------------------------------------ */
  function initMarquees() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.marquee'),
      function (marquee) {
        var track = marquee.querySelector('.marquee__track');
        if (!track) return;

        // Not enough cards to fill the row twice: drop the clones and
        // centre what is left instead of looping a visible gap.
        if (track.scrollWidth / 2 < marquee.clientWidth) {
          Array.prototype.forEach.call(
            track.querySelectorAll('.r-card--clone'),
            function (clone) {
              clone.remove();
            }
          );

          track.style.animation = 'none';
          track.style.width = '100%';
          track.style.justifyContent = 'center';
          track.style.flexWrap = 'wrap';
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     Shoppable videos — play only while on screen
     ------------------------------------------------------------------ */
  function initVideoShop() {
    var cards = document.querySelectorAll('.v-card');
    if (!cards.length) return;

    var reduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    Array.prototype.forEach.call(cards, function (card) {
      if (!claim(card, 'vzVideo')) return;

      var video = card.querySelector('[data-video]');
      var playBtn = card.querySelector('[data-video-play]');
      var soundBtn = card.querySelector('[data-video-sound]');

      if (!video) {
        if (playBtn) playBtn.remove();
        return;
      }

      // Muted must be set as a property too — the attribute alone is not
      // always enough for autoplay to be allowed.
      video.muted = true;

      function play() {
        var attempt = video.play();
        if (attempt && attempt.catch) {
          attempt.catch(function () {
            // Autoplay refused (low power mode, data saver): offer the button.
            card.classList.add('is-paused');
          });
        }
      }

      on(playBtn, 'click', function () {
        card.classList.remove('is-paused');
        play();
      });

      on(soundBtn, 'click', function () {
        var muting = !video.muted;
        video.muted = muting;
        soundBtn.setAttribute('aria-pressed', String(!muting));
        soundBtn.setAttribute(
          'aria-label',
          muting ? 'Unmute video' : 'Mute video'
        );
        // Turning sound on is a real interaction, so play if it was stopped.
        if (!muting && video.paused) play();
      });

      // Reduced motion, or no observer support: leave the poster up and let
      // the visitor start it themselves.
      if (reduced || !('IntersectionObserver' in window)) {
        card.classList.add('is-paused');
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              play();
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(card);
    });
  }

  /* ------------------------------------------------------------------
     Product gallery — thumbnail strip drives a scroll-snap stage
     ------------------------------------------------------------------ */
  function initGallery() {
    var galleries = document.querySelectorAll('[data-gallery]');
    if (!galleries.length) return;

    Array.prototype.forEach.call(galleries, function (gallery) {
      if (!claim(gallery, 'vzGallery')) return;

      var stage = gallery.querySelector('[data-gallery-stage]');
      var strip = gallery.querySelector('[data-gallery-thumbs]');
      var slides = gallery.querySelectorAll('[data-gallery-slide]');
      var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      if (!stage || slides.length < 2) return;

      // With more thumbnails than fit, keep the active one in view as the
      // shopper swipes the stage.
      function revealThumb(thumb) {
        if (!strip || strip.scrollWidth <= strip.clientWidth) return;
        var left = thumb.offsetLeft - strip.offsetLeft;
        var right = left + thumb.offsetWidth;
        var viewLeft = strip.scrollLeft;
        var viewRight = viewLeft + strip.clientWidth;

        if (left < viewLeft) {
          strip.scrollTo({ left: Math.max(0, left - 12), behavior: 'smooth' });
        } else if (right > viewRight) {
          strip.scrollTo({ left: right - strip.clientWidth + 12, behavior: 'smooth' });
        }
      }

      function setActive(index) {
        Array.prototype.forEach.call(thumbs, function (thumb) {
          var on = Number(thumb.dataset.galleryThumb) === index;
          thumb.classList.toggle('is-active', on);
          thumb.setAttribute('aria-current', String(on));
          if (on) revealThumb(thumb);
        });
      }

      function goTo(index) {
        var slide = slides[index];
        if (!slide) return;
        // scrollLeft rather than scrollIntoView, which would also drag the
        // whole page sideways on some browsers.
        stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: 'smooth' });
      }

      Array.prototype.forEach.call(thumbs, function (thumb) {
        on(thumb, 'click', function () {
          var index = Number(thumb.dataset.galleryThumb);
          setActive(index);
          goTo(index);
        });
      });

      // Keep the thumbnails in step when the stage is swiped or scrolled.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) {
                // Stop a video the moment it is swiped away.
                var playing = entry.target.querySelector('[data-gallery-video]');
                if (playing && !playing.paused) playing.pause();
                return;
              }
              setActive(Number(entry.target.dataset.index));
            });
          },
          { root: stage, threshold: 0.6 }
        );

        Array.prototype.forEach.call(slides, function (slide) {
          observer.observe(slide);
        });
      }

      // Left/right arrows move through the gallery when the stage has focus.
      on(stage, 'keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        var current = 0;
        Array.prototype.forEach.call(thumbs, function (thumb) {
          if (thumb.classList.contains('is-active')) {
            current = Number(thumb.dataset.galleryThumb);
          }
        });
        var next = e.key === 'ArrowRight' ? current + 1 : current - 1;
        if (next < 0 || next >= slides.length) return;
        e.preventDefault();
        setActive(next);
        goTo(next);
      });
    });
  }

  /* ------------------------------------------------------------------
     Sticky add to cart — mirrors the real buy button
     ------------------------------------------------------------------ */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    var mainBtn = document.querySelector('[data-buy-button]');
    if (!bar || !mainBtn || !claim(bar, 'vzSticky')) return;

    var buyRow = mainBtn.closest('.pdp__buy') || mainBtn;
    var barBtn = bar.querySelector('[data-sticky-add]');
    var barPrice = bar.querySelector('[data-sticky-price]');
    var mainPrice = document.querySelector('[data-product-price]');

    // Clicking the real button keeps one source of truth for the variant,
    // the pre-order property and the AJAX handler.
    on(barBtn, 'click', function () {
      if (mainBtn.disabled) return;
      mainBtn.click();
    });

    // The bar carries its own wording ("Add to Bag"), so the real button's text
    // is only borrowed when it is saying something the bar must not contradict:
    // Sold out, Pre-order, or the Adding…/Added feedback during a cart call.
    var ownLabel = barBtn ? barBtn.textContent.trim() : '';

    function sync() {
      if (!barBtn) return;

      barBtn.disabled = mainBtn.disabled;

      var borrow =
        mainBtn.disabled || mainBtn.classList.contains('btn--preorder');
      barBtn.textContent = borrow ? mainBtn.textContent.trim() : ownLabel;

      if (barPrice && mainPrice) barPrice.textContent = mainPrice.textContent;
    }

    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(mainBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true
      });
      if (mainPrice) {
        new MutationObserver(sync).observe(mainPrice, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    // The hidden state is visibility:hidden rather than the hidden attribute,
    // so the bar stays measurable while still being unfocusable and ignored
    // by screen readers.
    var footer = document.querySelector('.site-footer');

    function check() {
      // Reveal only once the buy row has gone ABOVE the viewport, not while it
      // is still below and simply not scrolled to yet.
      var past = buyRow.getBoundingClientRect().bottom < 0;

      // Stow only when the footer has actually risen far enough to sit under
      // the bar — not the moment its first pixel appears.
      var clash = false;
      if (footer) {
        var barHeight = bar.offsetHeight || 76;
        clash = footer.getBoundingClientRect().top <= window.innerHeight - barHeight;
      }

      bar.classList.toggle('is-visible', past && !clash);
    }

    on(window, 'scroll', check);
    on(window, 'resize', check);
    check();
    sync();
  }

  function init() {
    initMobileNav();
    initScrollCue();
    initVariantSelects();
    initAddToCart();
    initSort();
    initFilterPanel();
    initMarquees();
    initVideoShop();
    initGallery();
    initStickyAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', init);
})();